import {externalGuestDisplay} from './features/network/guestDisplay';
import {NetworkGuestPicker} from './features/network/Network';
import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./App.css";
import axios from "axios";
import {preloadOrderMenu, clearOrderMenuCache, installOrderMenuInvalidation} from "./services/orderMenuCache";
import { APP_VERSION } from "./version";
import ScrollToTop from "./components/Common/ScrollToTop";
import { FeedbackModal, FeedbackButton } from "./components/Common/FeedbackSystem";
import AnnouncementBanner from "./components/Common/AnnouncementBanner";
import EmployeeHistoryModal from "./components/Employee/EmployeeHistoryModal";
import { setAuth, getToken, getUser, clearAuth, expireSession, isTokenExpired, SESSION_EXPIRED_EVENT, PAYMENT_ACCESS_BLOCKED_EVENT, setupAxiosInterceptor, setOperatorSessionActive } from "./services/authService";
import { ClipboardList, LogOut, ShieldCheck, LockKeyhole } from "lucide-react";
import OperatorLoginDialog from './features/operator-auth/OperatorLoginDialog';
import OperatorSessionBar from './features/operator-auth/OperatorSessionBar';
import { endOperatorSession, expireOperatorSession, getOperatorSession, hasPersistedOperatorView } from './features/operator-auth/operatorAuthApi';
import { installHomeNavigation, navigateToActiveDashboard } from './homeNavigation';
import PaymentAccessBlockedDialog from './features/payment/PaymentAccessBlockedDialog';
import { getPaymentBlockedDetail } from './features/payment/paymentStatus';
import BlockedDepartmentAccessFlow from './features/payment/BlockedDepartmentAccessFlow';
import { useStationMode } from './components/Common/useStationMode';
import LoginLanding from './components/Common/LoginLanding';
import { isRegularDepartmentEmployee } from './components/Admin/employeeCategoryDisplay';
import InitialCredentialsModal from './components/Setup/InitialCredentialsModal';
import { getForcedPrelaunchSettings } from './features/prelaunch/runtimePrelaunch';
import ShoppingList from './components/Organization/ShoppingList';
import RecipeLibrary from './components/Organization/RecipeLibrary';
import EmployeeOrganizationTools from './components/Employee/EmployeeOrganizationTools';

// Use environment variable for backend URL
// In Docker: Use relative URL (empty string) so it works on any domain
// In Development: Use localhost:8001
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const HIDDEN_ORGANIZATION_FEATURES = {
  is_shopping_list_enabled: false,
  is_recipe_library_enabled: false,
};

// Setup axios interceptor for automatic token handling
setupAxiosInterceptor(axios);
installOrderMenuInvalidation(axios);

// Calculate displayed price after sponsoring using backend-like calculation
const calculateDisplayPrice = (item) => {
  if (!item.is_sponsored || item.is_sponsor_order) {
    return item.total_price; // Original price for non-sponsored or sponsor orders
  }
  
  // WICHTIG: Self-sponsoring Check
  const isSelfSponsored = item.sponsored_by_name && 
                          item.employee_name && 
                          item.sponsored_by_name === item.employee_name;
  
  if (isSelfSponsored) {
    return item.total_price; // Self-sponsored: pay full price
  }
  
  // SPEZIAL-CHECK: Welche Mahlzeit wurde gesponsert? (aus Message ermitteln)
  let onlyLunchSponsored = false;
  let onlyBreakfastSponsored = false;
  
  if (item.sponsored_message) {
    const message = item.sponsored_message.toLowerCase();
    
    // Prüfe ob NUR Mittagessen gesponsert wurde
    if (message.includes('mittagessen') && message.includes('ausgegeben') && !message.includes('frühstück')) {
      onlyLunchSponsored = true;
    }
    
    // Prüfe ob NUR Frühstück gesponsert wurde
    if (message.includes('frühstück') && message.includes('ausgegeben') && !message.includes('mittagessen')) {
      onlyBreakfastSponsored = true;
    }
  }
  
  // For sponsored orders, calculate remaining cost using backend-like logic
  let remainingCost = item.total_price;
  const sponsoredTypes = item.sponsored_meal_type ? item.sponsored_meal_type.split(',') : [];
  
  // Calculate exact sponsored amounts from readable_items (using total_price strings)
  if (item.readable_items && item.readable_items.length > 0) {
    for (const readableItem of item.readable_items) {
      const description = readableItem.description || '';
      const totalPriceStr = readableItem.total_price || '0.00 €';
      const itemPrice = parseFloat(totalPriceStr.replace(' €', '').replace(',', '.')) || 0;
      
      // Subtract breakfast items if sponsored (rolls and eggs, but NOT coffee)
      // ABER: NUR wenn NICHT onlyLunchSponsored
      if (sponsoredTypes.includes('breakfast') && !onlyLunchSponsored) {
        if ((description.includes('Brötchen') || 
             description.includes('Helle') || 
             description.includes('Körner') || 
             description.includes('Gekochte Eier') ||
             description.includes('Spiegeleier') ||
             description.includes('Rührei')) &&
            !description.includes('Kaffee')) {
          remainingCost -= itemPrice;
        }
      }
      
      // Subtract lunch cost if sponsored
      // ABER: NUR wenn NICHT onlyBreakfastSponsored
      if (sponsoredTypes.includes('lunch') && !onlyBreakfastSponsored && item.order_type === 'breakfast') {
        const isNotBreakfastItem = !description.includes('Kaffee') && 
                                   !description.includes('Brötchen') && 
                                   !description.includes('Helle') && 
                                   !description.includes('Körner') && 
                                   !description.includes('Ei') &&
                                   !description.includes('ei');  // CRITICAL: Also check lowercase 'ei' for "Spiegeleier"
        if (isNotBreakfastItem) {
          remainingCost -= itemPrice;
        }
      }
    }
  }
  
  return Math.max(0, remainingCost);
};

// Helper function to format date in German format
export const formatGermanDate = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const months = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];
  
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  
  return `${day}. ${month} ${year}`;
};

// Context for authentication
export const AuthContext = React.createContext();

const AuthProvider = ({ children }) => {
  const forcedPrelaunchSettings = useMemo(
    () => getForcedPrelaunchSettings(),
    []
  );
  const [currentDepartment, setCurrentDepartment] = useState(null);
  useEffect(() => {
    if (currentDepartment?.department_id) preloadOrderMenu(currentDepartment.department_id);
    else clearOrderMenuCache();
  }, [currentDepartment?.department_id]);
  const [isDepartmentAdmin, setIsDepartmentAdmin] = useState(false);
  const [isMasterAdmin, setIsMasterAdmin] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isOperatorChecking, setIsOperatorChecking] = useState(true);
  const [operatorSession, setOperatorSession] = useState(null);
  const [paymentAccessBlock, setPaymentAccessBlock] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState({ is_active: false, message: '' });
  const [prelaunchMode, setPrelaunchMode] = useState(() => (
    forcedPrelaunchSettings || {
      is_active: false,
      headline: 'FW Kantine für deine Wache',
      message: '',
      website_url: 'https://fw-kantine.de',
      email_token: null,
      has_email: false,
      whatsapp_number: null,
      whatsapp_message: ''
    }
  ));
  const [isCheckingMaintenance, setIsCheckingMaintenance] = useState(
    () => !forcedPrelaunchSettings
  );

  // LocalStorage keys for persistence
  const STORAGE_KEYS = {
    DEPARTMENT: 'fw_kantine_last_department',
    IS_DEPARTMENT_ADMIN: 'fw_kantine_is_department_admin',
    IS_MASTER_ADMIN: 'fw_kantine_is_master_admin'
  };

  // Check public operating modes on app start and poll for live updates.
  useEffect(() => {
    if (forcedPrelaunchSettings) return undefined;

    const checkOperatingModes = async () => {
      try {
        const [maintenanceResponse, prelaunchResponse] = await Promise.all([
          axios.get(`${API}/maintenance-mode`),
          axios.get(`${API}/prelaunch-mode`)
        ]);
        setMaintenanceMode({
          is_active: maintenanceResponse.data.is_maintenance_mode,
          message: maintenanceResponse.data.message || '🔧 Wartungsarbeiten werden durchgeführt. Bitte haben Sie etwas Geduld.'
        });
        setPrelaunchMode(prelaunchResponse.data);
      } catch (error) {
        console.error('Error checking operating modes:', error);
        setMaintenanceMode({ is_active: false, message: '' });
        setPrelaunchMode((current) => ({ ...current, is_active: false }));
      } finally {
        setIsCheckingMaintenance(false);
      }
    };

    checkOperatingModes();
    
    const pollInterval = setInterval(() => {
      checkOperatingModes();
    }, 10000);

    // Cleanup on unmount
    return () => {
      clearInterval(pollInterval);
    };
  }, [forcedPrelaunchSettings]);

  // Initialize app state from localStorage on mount
  useEffect(() => {
    if (forcedPrelaunchSettings) {
      setIsInitializing(false);
      return;
    }

    const initializeFromStorage = () => {
      try {
        const savedDepartment = localStorage.getItem(STORAGE_KEYS.DEPARTMENT);
        const savedIsDepartmentAdmin = localStorage.getItem(STORAGE_KEYS.IS_DEPARTMENT_ADMIN);
        const savedIsMasterAdmin = localStorage.getItem(STORAGE_KEYS.IS_MASTER_ADMIN);

        if (savedDepartment && savedIsDepartmentAdmin !== null) {
          const departmentData = JSON.parse(savedDepartment);
          setCurrentDepartment(departmentData);
          setIsDepartmentAdmin(savedIsDepartmentAdmin === 'true');
          setIsMasterAdmin(savedIsMasterAdmin === 'true');
        }
      } catch (error) {
        console.error('Fehler beim Laden der gespeicherten Sitzung:', error);
        // Clear invalid localStorage data
        Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
      } finally {
        setIsInitializing(false);
      }
    };

    initializeFromStorage();
  }, [forcedPrelaunchSettings]);

  // Restore the server-side, HttpOnly operator session independently of JWTs.
  useEffect(() => {
    if (forcedPrelaunchSettings) {
      setOperatorSessionActive(false);
      setIsOperatorChecking(false);
      return;
    }

    const savedDepartment = localStorage.getItem(STORAGE_KEYS.DEPARTMENT);
    if (!hasPersistedOperatorView(savedDepartment)) {
      setOperatorSessionActive(false);
      setIsOperatorChecking(false);
      return;
    }

    getOperatorSession()
      .then((session) => {
        setOperatorSession(session);
        setOperatorSessionActive(true);
      })
      .catch(() => {
        setOperatorSessionActive(false);
        const saved = localStorage.getItem(STORAGE_KEYS.DEPARTMENT);
        if (saved) {
          try {
            if (JSON.parse(saved)?.role === 'operator') {
              Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
              setCurrentDepartment(null);
              setIsDepartmentAdmin(false);
            }
          } catch {
            localStorage.removeItem(STORAGE_KEYS.DEPARTMENT);
          }
        }
      })
      .finally(() => setIsOperatorChecking(false));
  }, [forcedPrelaunchSettings]);

  const persistOperatorView = useCallback((department, admin) => {
    setCurrentDepartment(department);
    setIsDepartmentAdmin(admin);
    setIsMasterAdmin(true);
    localStorage.setItem(STORAGE_KEYS.DEPARTMENT, JSON.stringify(department));
    localStorage.setItem(STORAGE_KEYS.IS_DEPARTMENT_ADMIN, admin.toString());
    localStorage.setItem(STORAGE_KEYS.IS_MASTER_ADMIN, 'true');
  }, []);

  const activateOperatorSession = useCallback((session) => {
    clearAuth();
    setOperatorSession(session);
    setOperatorSessionActive(true);
    persistOperatorView({
      role: 'operator',
      operator_view: 'developer',
      department_id: 'developer',
      department_name: 'Developer Dashboard'
    }, false);
  }, [persistOperatorView]);

  const openOperatorDeveloper = useCallback(() => {
    persistOperatorView({
      role: 'operator',
      operator_view: 'developer',
      department_id: 'developer',
      department_name: 'Developer Dashboard'
    }, false);
  }, [persistOperatorView]);

  const openOperatorDepartment = useCallback((department, admin) => {
    persistOperatorView({
      role: 'operator',
      operator_view: admin ? 'department_admin' : 'employee',
      department_id: department.id,
      department_name: department.name
    }, admin);
  }, [persistOperatorView]);

  const loginDepartment = (departmentData) => {
    setCurrentDepartment(departmentData);
    setIsDepartmentAdmin(false);
    setIsMasterAdmin(false);
    
    // Store JWT token if present
    if (departmentData.token) {
      setAuth(departmentData.token, departmentData);
    }
    
    // Persist to localStorage
    localStorage.setItem(STORAGE_KEYS.DEPARTMENT, JSON.stringify(departmentData));
    localStorage.setItem(STORAGE_KEYS.IS_DEPARTMENT_ADMIN, 'false');
    localStorage.setItem(STORAGE_KEYS.IS_MASTER_ADMIN, 'false');
  };

  const loginDepartmentAdmin = (departmentData) => {
    setCurrentDepartment(departmentData);
    setIsDepartmentAdmin(true);
    setIsMasterAdmin(departmentData.access_level === "master");
    
    // Store JWT token if present
    if (departmentData.token) {
      setAuth(departmentData.token, departmentData);
    }
    
    // Persist to localStorage
    localStorage.setItem(STORAGE_KEYS.DEPARTMENT, JSON.stringify(departmentData));
    localStorage.setItem(STORAGE_KEYS.IS_DEPARTMENT_ADMIN, 'true');
    localStorage.setItem(STORAGE_KEYS.IS_MASTER_ADMIN, (departmentData.access_level === "master").toString());
  };

  const clearLocalSession = useCallback(() => {
    setOperatorSession(null);
    setOperatorSessionActive(false);
    setCurrentDepartment(null);
    setIsDepartmentAdmin(false);
    setIsMasterAdmin(false);
    
    // Clear JWT token
    clearAuth();
    
    // Clear localStorage
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
  }, []);

  const showPaymentAccessBlock = useCallback((detail) => {
    clearLocalSession();
    setPaymentAccessBlock(detail);
  }, [clearLocalSession]);

  const logout = useCallback(async () => {
    try {
      await endOperatorSession({
        active: Boolean(operatorSession),
        clearLocalState: clearLocalSession,
      });
      return true;
    } catch (error) {
      console.error('Betreibersitzung konnte nicht sicher beendet werden:', error);
      window.alert('Abmeldung konnte nicht bestätigt werden. Bitte Verbindung prüfen und erneut versuchen.');
      return false;
    }
  }, [operatorSession, clearLocalSession]);

  useEffect(() => {
    if (!operatorSession?.expires_at) return undefined;
    const expireAtDeadline = () => {
      void expireOperatorSession({ clearLocalState }).then(() => {
        window.alert('Ihre Betreibersitzung ist abgelaufen. Bitte erneut einloggen.');
      });
    };
    const remaining = new Date(operatorSession.expires_at).getTime() - Date.now();
    if (remaining <= 0) {
      expireAtDeadline();
      return undefined;
    }
    const timer = window.setTimeout(expireAtDeadline, remaining);
    return () => window.clearTimeout(timer);
  }, [operatorSession, clearLocalSession]);

  // A rejected or expired API session must immediately leave authenticated UI.
  useEffect(() => {
    const handleExpiredSession = () => {
      setCurrentDepartment(null);
      setIsDepartmentAdmin(false);
      setIsMasterAdmin(false);
      setOperatorSession(null);
      setOperatorSessionActive(false);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleExpiredSession);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleExpiredSession);
  }, []);

  // A Payment block on any protected request immediately removes tenant UI.
  useEffect(() => {
    const handlePaymentAccessBlocked = event => {
      showPaymentAccessBlock(event.detail || {});
    };
    window.addEventListener(PAYMENT_ACCESS_BLOCKED_EVENT, handlePaymentAccessBlocked);
    return () => window.removeEventListener(
      PAYMENT_ACCESS_BLOCKED_EVENT,
      handlePaymentAccessBlocked
    );
  }, [showPaymentAccessBlock]);

  // Check token expiry on mount and periodically
  useEffect(() => {
    const checkTokenExpiry = () => {
      if (
        currentDepartment &&
        currentDepartment.role !== 'operator' &&
        isTokenExpired()
      ) {
        console.warn('Token expired - logging out');
        expireSession();
      }
    };
    
    // Check on mount
    checkTokenExpiry();
    
    // Check every 5 minutes
    const interval = setInterval(checkTokenExpiry, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentDepartment]);

  const setAuthState = (state) => {
    if (state.currentDepartment) {
      setCurrentDepartment(state.currentDepartment);
    }
    if (state.hasOwnProperty('isDepartmentAdmin')) {
      setIsDepartmentAdmin(state.isDepartmentAdmin);
    }
    if (state.hasOwnProperty('isMasterAdmin')) {
      setIsMasterAdmin(state.isMasterAdmin);
    }
  };

  return (
    <AuthContext.Provider value={{
      currentDepartment,
      isDepartmentAdmin,
      isMasterAdmin,
      isInitializing: isInitializing || isOperatorChecking,
      operatorSession,
      maintenanceMode,
      prelaunchMode,
      forcedPrelaunchSettings,
      isCheckingMaintenance,
      loginDepartment,
      loginDepartmentAdmin,
      logout,
      activateOperatorSession,
      openOperatorDeveloper,
      openOperatorDepartment,
      showPaymentAccessBlock,
      setAuthState
    }}>
      {children}
      {paymentAccessBlock && (
        <PaymentAccessBlockedDialog
          detail={paymentAccessBlock}
          onClose={() => setPaymentAccessBlock(null)}
        />
      )}
    </AuthContext.Provider>
  );
};

// NumberSelector Component - Replaces number inputs with - and + buttons
export const NumberSelector = ({ 
  value, 
  onChange, 
  min = 0, 
  max = 99, 
  label = "",
  unit = "",
  className = "" 
}) => {
  const handleDecrement = () => {
    if (value > min) {
      onChange(value - 1);
    }
  };

  const handleIncrement = () => {
    if (value < max) {
      onChange(value + 1);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {label && <span className="text-sm font-medium mr-2">{label}:</span>}
      <button
        type="button"
        onClick={handleDecrement}
        disabled={value <= min}
        className="w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-bold text-lg"
      >
        −
      </button>
      <span className="min-w-[3rem] text-center font-medium text-lg">
        {value}
      </span>
      <button
        type="button"
        onClick={handleIncrement}
        disabled={value >= max}
        className="w-8 h-8 flex items-center justify-center bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-bold text-lg"
      >
        +
      </button>
      {unit && <span className="text-sm text-gray-600 ml-1">{unit}</span>}
    </div>
  );
};

// Success Notification Component
export const SuccessNotification = ({ message, onClose }) => {
  const [hasPlayed, setHasPlayed] = useState(false);

  useEffect(() => {
    // Play sound only once
    if (!hasPlayed) {
      playSucessSound();
      setHasPlayed(true);
    }
    
    // Auto-close after 1.5 seconds
    const timer = setTimeout(() => {
      if (onClose) {
        onClose();
      }
    }, 1000);
    
    // Cleanup timer on unmount
    return () => {
      clearTimeout(timer);
    };
  }, []); // Empty dependency array to run only once

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 py-8 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl p-6 mx-4 max-w-md w-full border-2 border-green-200">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
            <svg className="h-8 w-8 text-green-600 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Erfolgreich!</h3>
          <p className="text-gray-700">{message}</p>
          <div className="mt-4">
            <div className="bg-green-500 h-2 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Balance Warning Modal Component
export const BalanceWarningModal = ({ employeeName, openBalances, onClose, onConfirmDelete }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 py-8 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl p-6 mx-4 max-w-lg w-full border-2 border-amber-200">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 mb-4">
            <svg className="h-8 w-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-testid="balance-warning-title">Offene Salden vorhanden</h3>
          
          <div className="text-left mb-4">
            <p className="text-gray-700 mb-3">
              <strong>{employeeName}</strong> hat noch offene Salden:
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <ul className="space-y-1">
                {openBalances.map((balance, index) => (
                  <li key={index} className="text-amber-800 font-medium text-sm flex items-center">
                    <span className="text-amber-500 mr-2">&bull;</span>
                    {balance}
                  </li>
                ))}
              </ul>
            </div>
            
            <p className="text-gray-600 text-sm">
              Die Restsalden werden im Lösch-Log protokolliert und können im Developer Dashboard eingesehen werden.
            </p>
          </div>
          
          <div className="flex justify-center gap-3">
            <button
              onClick={onClose}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
              data-testid="balance-warning-cancel"
            >
              Abbrechen
            </button>
            <button
              onClick={onConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
              data-testid="balance-warning-confirm-delete"
            >
              Trotzdem löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sound Utility Functions
export const playSucessSound = () => {
  try {
    // Check if audio is enabled in localStorage (default: true)
    const isAudioEnabled = localStorage.getItem('canteenAudioEnabled') !== 'false';
    if (!isAudioEnabled) return;

    // Create success sound with two ascending tones (do-re pattern)
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // First tone (lower)
    const oscillator1 = audioContext.createOscillator();
    const gainNode1 = audioContext.createGain();
    oscillator1.connect(gainNode1);
    gainNode1.connect(audioContext.destination);
    
    oscillator1.frequency.setValueAtTime(523, audioContext.currentTime); // C5 note
    gainNode1.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
    
    oscillator1.start(audioContext.currentTime);
    oscillator1.stop(audioContext.currentTime + 0.15);
    
    // Second tone (higher) - starts slightly overlapped
    const oscillator2 = audioContext.createOscillator();
    const gainNode2 = audioContext.createGain();
    oscillator2.connect(gainNode2);
    gainNode2.connect(audioContext.destination);
    
    oscillator2.frequency.setValueAtTime(659, audioContext.currentTime + 0.08); // E5 note
    gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime + 0.08);
    gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
    
    oscillator2.start(audioContext.currentTime + 0.08);
    oscillator2.stop(audioContext.currentTime + 0.25);
  } catch (error) {
    console.log('Audio not supported or blocked:', error);
  }
};

// Helper function to ensure URL has protocol
const normalizeUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return '';
  
  // Check if URL already has a protocol
  if (trimmedUrl.match(/^https?:\/\//i)) {
    return trimmedUrl;
  }
  
  // Add https:// if missing
  return `https://${trimmedUrl}`;
};

// Employee history v1.16.2. The previous implementation stays exported during
// this uncommitted review phase so the UI can be reverted without data changes.
export const IndividualEmployeeProfile = ({ employee, onClose }) => {
  const { currentDepartment } = React.useContext(AuthContext);

  return (
    <EmployeeHistoryModal
      employee={employee}
      onClose={onClose}
      currentDepartment={currentDepartment}
      apiBase={API}
    />
  );
};

export const LegacyIndividualEmployeeProfile = ({ employee, onClose }) => {
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [allBalances, setAllBalances] = useState(null); // ERWEITERT für Subkonten
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [departments, setDepartments] = useState([]); // For 8H service employees to show all departments
  const [paypalSettings, setPaypalSettings] = useState({
    enabled: false,
    breakfast_enabled: false,
    drinks_enabled: false,
    use_separate_links: false,
    combined_link: '',
    breakfast_link: '',
    drinks_link: ''
  });
  // NEU: Department configuration
  const [departmentConfig, setDepartmentConfig] = useState({
    use_combined_accounts: false,
    is_breakfast_lunch_enabled: true,
    is_drinks_enabled: true,
    is_snacks_enabled: true
  });

  const { isSingleDepartment, loading: stationModeLoading } = useStationMode();
  const showOtherDepartments = !isSingleDepartment && !stationModeLoading;
  const { currentDepartment } = React.useContext(AuthContext);
  
  // Helper function to format balance with 2 decimal places
  const formatBalance = (balance) => {
    const numBalance = parseFloat(balance) || 0;
    const rounded = Math.round(numBalance * 100) / 100; // Round to 2 decimals
    const formatted = (rounded === -0 ? 0 : rounded).toFixed(2); // Avoid -0.00
    return `${formatted}€`;
  };
  
  // ERWEITERT: Prüfe ob es sich um einen temporären Gastmitarbeiter oder 8H-Dienst Mitarbeiter handelt
  const isTemporaryGuest = employee.isTemporary || (employee.department_id !== currentDepartment?.department_id && !employee.is_8h_service);
  const is8HService = employee.is_8h_service || false;

  // Fetch all departments for 8H service employees
  useEffect(() => {
    if (is8HService) {
      const fetchDepartments = async () => {
        try {
          const response = await axios.get(`${API}/departments`);
          setDepartments(response.data);
        } catch (error) {
          console.error('Error fetching departments:', error);
        }
      };
      fetchDepartments();
    }
  }, [is8HService]);

  useEffect(() => {
    fetchEmployeeProfile();
    fetchAllBalances(); // ERWEITERT: Lade auch alle Subkonten
    fetchPayPalSettings();
    fetchDepartmentConfig();
  }, [employee.id, currentDepartment?.department_id]); // ERWEITERT: Reload bei Department-Wechsel

  const fetchDepartmentConfig = async () => {
    try {
      if (currentDepartment?.department_id) {
        const response = await axios.get(`${API}/department-config/${currentDepartment.department_id}`);
        setDepartmentConfig({
          use_combined_accounts: response.data.use_combined_accounts,
          is_breakfast_lunch_enabled: response.data.is_breakfast_lunch_enabled,
          is_drinks_enabled: response.data.is_drinks_enabled,
          is_snacks_enabled: response.data.is_snacks_enabled
        });
      }
    } catch (error) {
      console.error('Fehler beim Laden der Department-Konfiguration:', error);
    }
  };

  const fetchAllBalances = async () => {
    try {
      const response = await axios.get(`${API}/employees/${employee.id}/all-balances`);
      setAllBalances(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Subkonten:', error);
    }
  };

  const fetchPayPalSettings = async () => {
    try {
      if (currentDepartment?.department_id) {
        const API_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
        const response = await axios.get(`${API_URL}/department-paypal-settings/${currentDepartment.department_id}`);
        setPaypalSettings(response.data);
      }
    } catch (error) {
      console.error('Fehler beim Laden der PayPal-Einstellungen:', error);
    }
  };

  const fetchEmployeeProfile = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/employees/${employee.id}/profile`);
      setEmployeeProfile(response.data);
    } catch (error) {
      console.error('Fehler beim Laden des Mitarbeiterprofils:', error);
      alert('Fehler beim Laden des Profils');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Berlin'
    });
  };

  const getOrderTypeLabel = (orderType) => {
    switch (orderType) {
      case 'breakfast': return 'Essen';
      case 'drinks': return 'Getränke';
      case 'sweets': return 'Snacks';
      default: return orderType;
    }
  };

  const getDepartmentName = (departmentId) => {
    // Helper function um Abteilungs-Namen aus ID zu bestimmen
    // DYNAMICALLY extracts department number from ID (e.g., fw4abteilung1 OR fw9100abteilung1 → "1. WA")
    // Supports multi-tenant setups with any wache_nummer
    const match = departmentId.match(/abteilung(\d+)$/);
    if (match) {
      return `${match[1]}. WA`;
    }
    return departmentId; // Fallback to original ID if pattern doesn't match
  };

  const isOrderCancellable = (order) => {
    // Check if already cancelled
    if (order.is_cancelled) {
      return false;
    }
    
    // Check if order is from today (Berlin timezone)
    const orderDate = new Date(order.timestamp);
    const today = new Date();
    
    // Convert to Berlin timezone for comparison (approximation)
    const orderDateString = orderDate.toLocaleDateString('de-DE');
    const todayString = today.toLocaleDateString('de-DE');
    
    return orderDateString === todayString;
  };

  // ERWEITERT: Combine and sort orders and payments chronologically mit Department-Filterung
  const getCombinedHistory = () => {
    if (!employeeProfile) return [];
    
    const orders = (employeeProfile.order_history || []).map(order => ({
      ...order,
      type: 'order',
      timestamp: order.timestamp
    }));
    
    const payments = (employeeProfile.payment_history || []).map(payment => ({
      ...payment,
      type: 'payment',
      timestamp: payment.timestamp
    }));
    
    // ERWEITERT: Unterschiedliche Filterung je nach Mitarbeiter-Typ
    let filteredOrders = orders;
    
    if (isTemporaryGuest || isSingleDepartment) {
      // Für temporäre Gastmitarbeiter: Nur Bestellungen der aktuellen Gast-Wachabteilung anzeigen
      filteredOrders = orders.filter(order => order.department_id === currentDepartment?.department_id);
    }
    // Für Stammmitarbeiter: Alle Bestellungen anzeigen (keine Filterung)
    
    // Combine and sort by timestamp (newest first)
    const visiblePayments = isSingleDepartment ? payments.filter(payment => !payment.department_id || payment.department_id === currentDepartment?.department_id) : payments;
    const combined = [...filteredOrders, ...visiblePayments];
    combined.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    return combined;
  };

  // Pagination logic
  const combinedHistory = getCombinedHistory();
  const totalPages = Math.ceil(combinedHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = combinedHistory.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToFirstPage = () => {
    setCurrentPage(1);
  };

  const handleDeleteOrder = async (orderId, orderType, totalPrice) => {
    if (window.confirm('Bestellung wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      try {
        await axios.delete(`${API}/employee/${employee.id}/orders/${orderId}`);
        
        setSuccessMessage('Bestellung erfolgreich storniert!');
        setShowSuccessNotification(true);
        
        // Refresh employee profile immediately after successful deletion
        fetchEmployeeProfile();
        
      } catch (error) {
        console.error('Fehler beim Löschen der Bestellung:', error);
        
        let errorMessage = 'Fehler beim Löschen der Bestellung';
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        }
        alert(errorMessage);
      }
    }
  };

  // State for success notification in profile
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Balance Warning Modal State
  const [showBalanceWarning, setShowBalanceWarning] = useState(false);
  const [balanceWarningData, setBalanceWarningData] = useState({ employeeName: '', openBalances: [] });

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 py-8 overflow-y-auto">
        <div className="bg-white rounded-lg p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Lade Profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!employeeProfile) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 py-8 overflow-y-auto">
        <div className="bg-white rounded-lg p-8">
          <p>Fehler beim Laden des Profils</p>
          <button onClick={onClose} className="mt-4 bg-red-600 text-white px-4 py-2 rounded">
            Schließen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 py-8 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg max-w-6xl w-full mx-4 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">
              {employeeProfile.employee.name} - Verlauf
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* ERWEITERTE Balance Overview - UNTERSCHIEDLICH für Gast vs. Stamm vs. 8H */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">💰 Kontostände</h3>
            
            {is8HService ? (
              /* 8H-DIENST: Zeige alle Subkonten aller Wachabteilungen (auch mit 0€) */
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-600 mb-3">{isSingleDepartment ? 'Saldo' : 'Übergreifend: Subkonten aller Wachabteilungen'}</h4>
                {departments && departments.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {departments.filter(dept => !isSingleDepartment || dept.id === currentDepartment?.department_id).map((dept) => {
                      const deptId = dept.id;
                      
                      // Get balances from subaccount_balances or default to 0
                      const balances = allBalances?.subaccount_balances?.[deptId] || {};
                      
                      // Korrekte Umwandlung: fw4abteilung1 OR fw9100abteilung1 -> 1. WA
                      const match = deptId.match(/abteilung(\d+)$/);
                      const deptNumber = match ? match[1] : deptId;
                      const deptName = dept.name || `${deptNumber}. WA`;
                      
                      // Check if this department uses combined accounts
                      // Use the flag from all-balances API (most reliable) or department config
                      const isCombined = balances.use_combined !== undefined ? balances.use_combined : (dept.use_combined_accounts || false);
                      const totalBalance = isCombined 
                        ? (balances.combined || 0) 
                        : (balances.breakfast || 0) + (balances.drinks || 0);
                      
                      return (
                        <div key={deptId} className={`border rounded-lg p-3 ${deptId === currentDepartment?.department_id ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}>
                          <div className="text-center mb-3">
                            <h5 className="text-sm font-medium text-gray-700">
                              {deptName} {deptId === currentDepartment?.department_id && '(Aktuell)'}
                            </h5>
                            <div className={`text-sm font-semibold ${(totalBalance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              Gesamt: {(totalBalance || 0).toFixed(2)}€
                            </div>
                          </div>
                          
                          {isCombined ? (
                            /* COMBINED ACCOUNTS: Zeige nur Gesamtsaldo */
                            <div className="text-center p-3 bg-green-50 rounded border border-green-200">
                              <div className={`text-base font-medium ${(balances.combined || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {(balances.combined || 0).toFixed(2)}€
                              </div>
                              <div className="text-xs text-gray-500">Gesamtsaldo</div>
                            </div>
                          ) : (
                            /* SEPARATE ACCOUNTS: Zeige getrennte Konten */
                            <div className="grid grid-cols-2 gap-2">
                              <div className="text-center p-2 bg-white rounded border">
                                <div className={`text-sm font-medium ${(balances.breakfast || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {(balances.breakfast || 0).toFixed(2)}€
                                </div>
                                <div className="text-xs text-gray-500">Essen</div>
                              </div>
                              <div className="text-center p-2 bg-white rounded border">
                                <div className={`text-sm font-medium ${(balances.drinks || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {(balances.drinks || 0).toFixed(2)}€
                                </div>
                                <div className="text-xs text-gray-500">Getränke/Snacks</div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-600">Keine Departments verfügbar</p>
                )}
              </div>
            ) : isTemporaryGuest ? (
              /* GASTWACHABTEILUNG: Subkonto-Saldo (kombiniert ODER getrennt je nach Department-Config) */
              <div className="mb-4">
                <h4 className="text-md font-medium text-purple-800 mb-3">
                  👥 Gastwachabteilung: {currentDepartment?.department_name}
                </h4>
                {allBalances && allBalances.subaccount_balances && allBalances.subaccount_balances[currentDepartment?.department_id] ? (
                  allBalances.subaccount_balances[currentDepartment.department_id].use_combined ? (
                    /* COMBINED: Zeige combined Feld */
                    <div className="border border-gray-300 rounded-lg p-4 bg-blue-50 border-blue-200">
                      <h3 className="font-semibold text-blue-800">Gesamtsaldo (Gastkonto)</h3>
                      <p className={`text-2xl font-bold ${
                        (allBalances.subaccount_balances[currentDepartment.department_id].combined || 0) >= 0 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {(allBalances.subaccount_balances[currentDepartment.department_id].combined || 0).toFixed(2)} €
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        Alle Buchungen (Frühstück + Getränke) auf diesem Konto
                      </p>
                    </div>
                  ) : (
                    /* SEPARATE: Zeige getrennte Konten */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className={`border border-gray-300 rounded-lg p-4 ${
                        (allBalances.subaccount_balances[currentDepartment.department_id]?.breakfast || 0) >= 0 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <h3 className={`font-semibold ${
                          (allBalances.subaccount_balances[currentDepartment.department_id]?.breakfast || 0) >= 0 
                            ? 'text-green-800' 
                            : 'text-red-800'
                        }`}>Essen Saldo</h3>
                        <p className={`text-2xl font-bold ${
                          (allBalances.subaccount_balances[currentDepartment.department_id]?.breakfast || 0) >= 0 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>{(allBalances.subaccount_balances[currentDepartment.department_id]?.breakfast || 0).toFixed(2)} €</p>
                      </div>
                      <div className={`border border-gray-300 rounded-lg p-4 ${
                        (allBalances.subaccount_balances[currentDepartment.department_id]?.drinks || 0) >= 0 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <h3 className={`font-semibold ${
                          (allBalances.subaccount_balances[currentDepartment.department_id]?.drinks || 0) >= 0 
                            ? 'text-green-800' 
                            : 'text-red-800'
                        }`}>Getränke/Snacks Saldo</h3>
                        <p className={`text-2xl font-bold ${
                          (allBalances.subaccount_balances[currentDepartment.department_id]?.drinks || 0) >= 0 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>{(allBalances.subaccount_balances[currentDepartment.department_id]?.drinks || 0).toFixed(2)} €</p>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    Keine Subkonto-Daten für diese Gastwachabteilung verfügbar
                  </div>
                )}
              </div>
            ) : (
              /* STAMMWACHABTEILUNG: Haupt + alle Subkonten */
              <>
                {/* Stammwachabteilung (groß) */}
                <div className="mb-6">
                  <h4 className="text-md font-medium text-blue-800 mb-3">
                    🏠 Stammwachabteilung: {allBalances?.main_department_name || currentDepartment?.department_name}
                  </h4>
                  
                  {departmentConfig.use_combined_accounts ? (
                    /* COMBINED ACCOUNTS: Ein gemeinsames Konto */
                    <div className="grid grid-cols-1 gap-4">
                      <div className={`border border-gray-300 rounded-lg p-4 ${
                        (employeeProfile.employee?.combined_balance || 0) >= 0 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <h3 className={`font-semibold ${
                          (employeeProfile.employee?.combined_balance || 0) >= 0 
                            ? 'text-green-800' 
                            : 'text-red-800'
                        }`}>Gesamtsaldo</h3>
                        <p className={`text-2xl font-bold ${
                          (employeeProfile.employee?.combined_balance || 0) >= 0 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>{(employeeProfile.employee?.combined_balance || 0).toFixed(2)} €</p>
                        <p className="text-xs text-gray-500 mt-1">Essen, Getränke & Snacks</p>
                        
                        {/* PayPal Button for Combined Account */}
                        {paypalSettings.enabled && (employeeProfile.employee?.combined_balance || 0) < 0 && (
                          <div className="mt-3">
                            {(() => {
                              const link = paypalSettings.use_separate_links ? paypalSettings.breakfast_link : paypalSettings.combined_link;
                              if (link && link.trim()) {
                                return (
                                  <a
                                    href={normalizeUrl(link)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
                                  >
                                    💳 Bezahlen mit PayPal
                                  </a>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* SEPARATE ACCOUNTS: Getrennte Konten wie bisher */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className={`border border-gray-300 rounded-lg p-4 ${
                        employeeProfile.breakfast_total >= 0 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <h3 className={`font-semibold ${
                          employeeProfile.breakfast_total >= 0 
                            ? 'text-green-800' 
                            : 'text-red-800'
                        }`}>Essen Saldo</h3>
                        <p className={`text-2xl font-bold ${
                          (employeeProfile.breakfast_total || 0) >= 0 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>{(employeeProfile.breakfast_total || 0).toFixed(2)} €</p>
                        
                        {/* PayPal Button for Breakfast */}
                        {paypalSettings.enabled && paypalSettings.breakfast_enabled && (employeeProfile.breakfast_total || 0) < 0 && (
                          <div className="mt-3">
                            {(() => {
                              let link = '';
                              if (paypalSettings.use_separate_links) {
                                link = paypalSettings.breakfast_link;
                              } else {
                                link = paypalSettings.combined_link;
                              }
                              
                              if (link && link.trim()) {
                                return (
                                  <a
                                    href={normalizeUrl(link)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
                                  >
                                    💳 Bezahlen mit PayPal
                                  </a>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                      </div>
                      <div className={`border border-gray-300 rounded-lg p-4 ${
                        (employeeProfile.drinks_sweets_total || 0) >= 0 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <h3 className={`font-semibold ${
                          (employeeProfile.drinks_sweets_total || 0) >= 0 
                            ? 'text-green-800' 
                            : 'text-red-800'
                        }`}>Getränke/Snacks Saldo</h3>
                        <p className={`text-2xl font-bold ${
                          (employeeProfile.drinks_sweets_total || 0) >= 0 
                            ? 'text-green-600' 
                            : 'text-red-600'
                        }`}>{(employeeProfile.drinks_sweets_total || 0).toFixed(2)} €</p>
                        
                        {/* PayPal Button for Drinks */}
                        {paypalSettings.enabled && paypalSettings.drinks_enabled && (employeeProfile.drinks_sweets_total || 0) < 0 && (
                          <div className="mt-3">
                            {(() => {
                              let link = '';
                              if (paypalSettings.use_separate_links) {
                                link = paypalSettings.drinks_link;
                              } else {
                                link = paypalSettings.combined_link;
                              }
                              
                              if (link && link.trim()) {
                                return (
                                  <a
                                    href={normalizeUrl(link)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 bg-purple-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-purple-700 transition-colors"
                                  >
                                    💳 Bezahlen mit PayPal
                                  </a>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Subkonten (andere Wachabteilungen) - Nebeneinander auf gleicher Breite wie Stammsaldos */}
                {showOtherDepartments && allBalances && allBalances.subaccount_balances && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-600 mb-3">👥 Subkonten (andere Wachabteilungen):</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {Object.entries(allBalances.subaccount_balances).map(([deptId, balances]) => {
                        // Skip main department (already shown above)
                        if (deptId === allBalances.main_department_id) return null;
                        
                        const isCombined = balances.use_combined || false;
                        
                        return (
                          <div key={deptId} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                            <div className="text-center mb-3">
                              <h5 className="text-sm font-medium text-gray-700 truncate" title={balances.department_name}>
                                {balances.department_name}
                              </h5>
                              <div className={`text-sm font-semibold ${(balances.total || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                Gesamt: {(balances.total || 0).toFixed(2)}€
                              </div>
                            </div>
                            
                            {isCombined ? (
                              /* COMBINED ACCOUNTS: Zeige nur ein Konto */
                              <div className="text-center p-2 bg-green-50 rounded border border-green-200">
                                <div className={`text-sm font-medium ${(balances.combined || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {(balances.combined || 0).toFixed(2)}€
                                </div>
                                <div className="text-xs text-gray-600">Gesamtsaldo</div>
                              </div>
                            ) : (
                              /* SEPARATE ACCOUNTS: Zeige getrennte Konten */
                              <div className="grid grid-cols-2 gap-2">
                                <div className="text-center p-2 bg-white rounded border">
                                  <div className={`text-sm font-medium ${(balances.breakfast || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {(balances.breakfast || 0).toFixed(2)}€
                                  </div>
                                  <div className="text-xs text-gray-600">Essen</div>
                                </div>
                                <div className="text-center p-2 bg-white rounded border">
                                  <div className={`text-sm font-medium ${(balances.drinks || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {(balances.drinks || 0).toFixed(2)}€
                                  </div>
                                  <div className="text-xs text-gray-600">Getränke</div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Combined Chronological History */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Chronologischer Verlauf</h3>
              <span className="text-sm text-gray-600">
                Seite {currentPage} von {totalPages} ({combinedHistory.length} Einträge gesamt)
              </span>
            </div>
            
            {combinedHistory.length === 0 ? (
              <p className="text-gray-600 text-center py-8">Keine Einträge vorhanden</p>
            ) : (
              <div className="space-y-4">
                {currentItems.map((item, index) => {
                  if (item.type === 'order') {
                    // Check if order is cancelled
                    const isCancelled = item.is_cancelled;
                    const isResetForEdit = isCancelled && item.cancellation_reason === 'reset_for_edit';
                    const cardStyle = isCancelled 
                      ? (isResetForEdit ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200") 
                      : "bg-gray-50 border-gray-200";
                    const textStyle = isCancelled ? "line-through" : "";
                    
                    // Render Order (normal or cancelled)
                    return (
                      <div key={`order-${item.id || index}`} className={`${cardStyle} border rounded-lg p-4`}>
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <span className={`inline-block ${
                              isResetForEdit ? 'bg-amber-100 text-amber-800' :
                              isCancelled ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                            } text-xs font-semibold mr-2 px-2.5 py-0.5 rounded`}>
                              {isResetForEdit ? 'Zum Bearbeiten zurückgesetzt' : isCancelled ? 'Storniert' : getOrderTypeLabel(item.order_type)}
                            </span>
                            {/* ERWEITERT: Abteilungsmarkierung für Stammmitarbeiter und 8H-Mitarbeiter */}
                            {item.department_id && (
                              // For 8H employees: ALWAYS show department marker
                              // For regular employees: show marker if order is from different department
                              (is8HService || (!employee.isTemporary && item.department_id !== employee.department_id))
                            ) && (
                              <span className="inline-block bg-purple-100 text-purple-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">
                                {getDepartmentName(item.department_id)}
                              </span>
                            )}
                            <span className="text-sm text-gray-600">{formatDate(item.timestamp)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <p className={`font-semibold ${isCancelled ? 'text-red-600' : 'text-red-600'}`}>
                                {(() => {
                                  const displayPrice = calculateDisplayPrice(item);
                                  if (displayPrice === 0) {
                                    return '0.00 €';
                                  }
                                  // KORRIGIERT: Minus nur hinzufügen wenn displayPrice positiv ist
                                  if (displayPrice > 0) {
                                    return `-${displayPrice.toFixed(2)} €`;
                                  } else {
                                    return `${displayPrice.toFixed(2)} €`;
                                  }
                                })()}
                              </p>
                            </div>
                            {/* Delete button only for cancellable orders */}
                            {!isCancelled && isOrderCancellable(item) && (
                              <button
                                onClick={() => handleDeleteOrder(item.id, item.order_type, item.total_price)}
                                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 flex-shrink-0"
                                title="Bestellung löschen"
                              >
                                Löschen
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {/* Show cancellation info if cancelled */}
                        {isCancelled && (
                          <div className={`mb-2 text-sm p-2 rounded ${
                            isResetForEdit 
                              ? 'text-amber-700 bg-amber-100' 
                              : 'text-red-700 bg-red-100'
                          }`}>
                            {isResetForEdit ? (
                              <strong>Wurde zum Bearbeiten zurückgesetzt</strong>
                            ) : (
                              <strong>Storniert durch {item.cancelled_by_name}</strong>
                            )}
                            {' '}am {formatDate(item.cancelled_at)}
                          </div>
                        )}
                        
                        {/* Show sponsored info if sponsored OR sponsor order */}
                        {(item.is_sponsored || item.is_sponsor_order) && (
                          <div className="mb-2 text-sm text-green-700 bg-green-100 p-2 rounded">
                            {item.sponsored_message || (item.is_sponsor_order ? 
                              `${item.sponsor_message || 'Frühstück wurde an alle Kollegen ausgegeben, vielen Dank!'}` :
                              `Dieses ${item.order_type === 'breakfast' ? 'Frühstück' : 'Mittagessen'} wurde von ${item.sponsored_by_name} ausgegeben, bedanke dich bei ihm!`
                            )}
                            {/* Show detailed sponsoring breakdown for sponsor orders */}
                            {item.is_sponsor_order && item.readable_items && item.readable_items.length > 0 && (
                              <div className="mt-1 text-xs text-green-600">
                                {item.readable_items[0].description}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {item.readable_items && item.readable_items.length > 0 && (
                          <div className={`space-y-1 ${textStyle}`}>
                            {item.readable_items.map((orderItem, idx) => {
                              // WICHTIG: Self-sponsoring Check - wenn jemand sein eigenes Essen sponsert
                              const isSelfSponsored = item.is_sponsored && 
                                                     item.sponsored_by_name && 
                                                     employee && employee.name &&
                                                     item.sponsored_by_name === employee.name;
                              
                              // Backend liefert pro readable_item ein is_sponsored Flag,
                              // das die aktuelle Sponsoring-Konfiguration berücksichtigt
                              // (z.B. Kaffee wird nur strike-through wenn include_coffee aktiv war).
                              const isSponsoredItem = !isSelfSponsored && !item.is_sponsor_order && !!orderItem.is_sponsored;
                              const isToppingsSponsored = !isSelfSponsored && !item.is_sponsor_order && !!orderItem.toppings_is_sponsored;
                              
                              return (
                                <div key={idx} className="text-sm flex justify-between items-start">
                                  <div className="flex-1">
                                    <span className={`font-medium ${isSponsoredItem ? 'line-through text-gray-500' : ''}`}>
                                      {orderItem.description}
                                    </span>
                                    {orderItem.toppings && (
                                      <span className={`text-gray-600 block text-xs ${isToppingsSponsored ? 'line-through text-gray-400' : ''}`}>
                                        mit {orderItem.toppings}
                                      </span>
                                    )}
                                    {orderItem.unit_price && (
                                      <span className={`text-gray-500 block text-xs ${isSponsoredItem ? 'line-through text-gray-400' : ''}`}>
                                        ({orderItem.unit_price})
                                      </span>
                                    )}
                                  </div>
                                  {orderItem.total_price && (
                                    <span className={`text-sm font-medium text-right ml-2 ${isSponsoredItem ? 'line-through text-gray-400' : ''}`}>
                                      {orderItem.total_price}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {item.notes && item.notes.trim() !== '' && (
                          <div className="mt-3 pt-3 border-t border-gray-300">
                            <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                              <span className="text-xs font-medium text-yellow-800 block mb-1">📝 Extras & Sonderwünsche:</span>
                              <span className="text-sm text-yellow-700">{item.notes}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  } else if (item.order_type === 'deletion') {
                    // Render Deletion Entry (Red styling for cancellations)
                    return (
                      <div key={`deletion-${item.id || index}`} className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <span className="inline-block bg-red-100 text-red-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">
                              Stornierung
                            </span>
                            <span className="text-sm text-gray-600">{formatDate(item.timestamp)}</span>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-red-600">{item.total_price.toFixed(2)} €</p>
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-700">
                          <p><strong>Grund:</strong> {item.readable_items?.[0]?.description || 'Bestellung storniert'}</p>
                          <p><strong>Von:</strong> {item.deleted_by === 'employee' ? 'Mitarbeiter' : 'Admin'} ({item.deleted_by_name})</p>
                        </div>
                      </div>
                    );
                  } else {
                    // Render Payment (Green styling)
                    return (
                      <div key={`payment-${item.id || index}`} className={`border rounded-lg p-4 ${item.amount >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <span className={`inline-block text-xs font-semibold mr-2 px-2.5 py-0.5 rounded ${item.amount >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {item.amount >= 0 ? 'Einzahlung' : 'Auszahlung'}
                            </span>
                            {/* Department Badge - show for 8H employees or when payment is from different department */}
                            {item.department_id && (
                              (is8HService || (!employee.isTemporary && item.department_id !== employee.department_id))
                            ) && (
                              <span className="inline-block bg-purple-100 text-purple-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded">
                                {getDepartmentName(item.department_id)}
                              </span>
                            )}
                            <span className="text-sm text-gray-600">{formatDate(item.timestamp)}</span>
                          </div>
                          <div className="text-right">
                            <p className={`font-semibold ${item.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {item.amount >= 0 ? '+' : '-'}{Math.abs(item.amount).toFixed(2)} €
                            </p>
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-700">
                          <p><strong>Art:</strong> {
                            item.payment_type === 'combined' 
                              ? 'Gesamtkonto' 
                              : (item.payment_type === 'breakfast' ? 'Essen' : 'Getränke/Snacks')
                          }</p>
                          <p><strong>Admin:</strong> {item.admin_user}</p>
                          {item.booked_by && <p><strong>Gebucht von:</strong> {item.booked_by}</p>}
                          {item.notes && <p><strong>Hinweise:</strong> {item.notes}</p>}
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-center items-center space-x-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Zurück
              </button>
              
              {currentPage > 1 && (
                <button
                  onClick={goToFirstPage}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  Erste Seite
                </button>
              )}
              
              <span className="px-4 py-2 bg-blue-600 text-white rounded">
                {currentPage}
              </span>
              
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Vor →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Success Notification for Profile */}
      {showSuccessNotification && (
        <SuccessNotification
          message={successMessage}
          onClose={() => {
            setShowSuccessNotification(false);
            setSuccessMessage('');
          }}
        />
      )}
    </div>
  );
};

// Homepage with department cards - Password Required
// ==================== SETUP WIZARD COMPONENT ====================

const SetupWizard = ({ onSetupComplete }) => {
  const [wacheName, setWacheName] = useState('');
  const [wacheNummer, setWacheNummer] = useState('');
  const [anzahlAbteilungen, setAnzahlAbteilungen] = useState(4);
  const [abteilungsname, setAbteilungsname] = useState('');
  const [bootstrapSecret, setBootstrapSecret] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [initialCredentials, setInitialCredentials] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Prepare setup data
      const setupData = {
        wache_name: wacheName,
        wache_nummer: wacheNummer,
        anzahl_abteilungen: anzahlAbteilungen,
        abteilungsname: anzahlAbteilungen === 1 ? (abteilungsname.trim() || wacheName.trim()) : null
      };

      // Call setup endpoint
      const response = await axios.post(`${API}/setup/initial-setup`, setupData, {
        headers: { 'X-Bootstrap-Secret': bootstrapSecret }
      });

      if (response.data.success) {
        setInitialCredentials(response.data.initial_credentials);
      }
    } catch (error) {
      console.error('Setup-Fehler:', error);
      setError(
        error.response?.data?.detail || 
        'Fehler beim Setup. Bitte versuchen Sie es erneut.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
      {initialCredentials && (
        <InitialCredentialsModal
          credentials={initialCredentials}
          onConfirm={() => {
            setInitialCredentials(null);
            onSetupComplete();
          }}
        />
      )}
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <span className="text-3xl">🚒</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Willkommen zur Ersteinrichtung
          </h1>
          <p className="text-gray-600">
            Konfigurieren Sie Ihre Kantine-Feuerwache
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Bootstrap authentication */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Bootstrap-Secret
            </label>
            <input
              type="password"
              value={bootstrapSecret}
              onChange={(e) => setBootstrapSecret(e.target.value)}
              autoComplete="off"
              placeholder="Secret aus der Server-Konfiguration"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              Wird nur für diese Ersteinrichtung übertragen und nicht gespeichert.
            </p>
          </div>

          {/* Wache Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              🚒 Name der Wache
            </label>
            <input
              type="text"
              value={wacheName}
              onChange={(e) => setWacheName(e.target.value)}
              placeholder="z.B. Kantine Feuerwache"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              Dieser Name wird als App-Titel angezeigt
            </p>
          </div>

          {/* Wache Nummer */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              🔢 Kennung der Einrichtung
            </label>
            <input
              type="text"
              pattern="([0-9]+|[A-Za-z][A-Za-z0-9]*)"
              value={wacheNummer}
              onChange={(e) => setWacheNummer(e.target.value)}
              placeholder="z.B. 5430 oder rw25"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              Eine eindeutige Kennung, zum Beispiel 5430 oder rw25. Der Name wird unabhängig davon angezeigt.
            </p>
          </div>

          {/* Anzahl Abteilungen */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              👥 Anzahl Wachabteilungen
            </label>
            <select
              value={anzahlAbteilungen}
              onChange={(e) => setAnzahlAbteilungen(parseInt(e.target.value))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={1}>1 Wachabteilung</option>
              <option value={2}>2 Wachabteilungen</option>
              <option value={3}>3 Wachabteilungen</option>
              <option value={4}>4 Wachabteilungen</option>
            </select>
          </div>

          {anzahlAbteilungen === 1 && (
            <div>
              <label htmlFor="single-department-name" className="block text-sm font-semibold text-gray-700 mb-2">Name der Abteilung</label>
              <input id="single-department-name" value={abteilungsname} onChange={event => setAbteilungsname(event.target.value)} placeholder={wacheName || 'z.B. FF Wilhelmshagen'} className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              <p className="text-sm text-gray-500 mt-1">Ohne Eingabe wird der Name der Einrichtung verwendet. Funktionen für andere Wachabteilungen werden ausgeblendet.</p>
            </div>
          )}
          {/* Password Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 font-medium mb-2">
              Sichere Startpasswörter
            </p>
            <p className="text-sm text-blue-700">
              Für jede Abteilung werden getrennte, zufällige Mitarbeiter- und
              Admin-Passwörter erzeugt. Sie werden nur einmal nach Abschluss
              angezeigt und bereits gehasht gespeichert.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">❌ {error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-4 rounded-lg font-semibold text-white transition-all ${
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg transform hover:scale-[1.02]'
            }`}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Installation läuft...
              </span>
            ) : (
              '✅ Installation abschließen'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// ==================== HOMEPAGE COMPONENT ====================

const Homepage = () => {
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [departmentLoadError, setDepartmentLoadError] = useState('');
  const [blockedDepartment, setBlockedDepartment] = useState(null);
  const {
    loginDepartment,
    loginDepartmentAdmin,
    showPaymentAccessBlock,
    maintenanceMode,
    prelaunchMode,
    forcedPrelaunchSettings,
    isCheckingMaintenance
  } = React.useContext(AuthContext);
  
  // Title and Logo are now static - edit in /app/frontend/public/index.html and /app/frontend/public/logo.png
  
  // Setup State
  const [needsSetup, setNeedsSetup] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(
    () => !forcedPrelaunchSettings
  );

  useEffect(() => {
    if (forcedPrelaunchSettings) return undefined;
    checkSetupStatus();
    return undefined;
  }, [forcedPrelaunchSettings]);
  
  const checkSetupStatus = async () => {
    try {
      const response = await axios.get(`${API}/setup/needs-setup`);
      setNeedsSetup(response.data.needs_setup);
      
      if (!response.data.needs_setup) {
        // Setup already done, fetch departments
        fetchDepartments();
      }
    } catch (error) {
      console.error('Fehler beim Prüfen des Setup-Status:', error);
      // On error, assume no setup needed and try to load departments
      setNeedsSetup(false);
      fetchDepartments();
    } finally {
      setCheckingSetup(false);
    }
  };


  const initializeData = async () => {
    // ⚠️ DEAKTIVIERT: Diese Funktion löschte die Datenbank bei jedem Aufruf!
    // Nur für Development verwenden, niemals in Production!
    console.warn('🚨 initializeData() deaktiviert - gefährlich für Production-Daten!');
    return;
    
    try {
      await axios.post(`${API}/init-data`);
    } catch (error) {
      console.error('Fehler beim Initialisieren der Daten:', error);
    }
  };

  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    setDepartmentLoadError('');
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      setDepartmentLoadError('Wachabteilungen konnten nicht geladen werden. Bitte erneut versuchen.');
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleDepartmentLogin = async (department, password) => {
    try {
      const response = await axios.post(`${API}/login/department`, {
        department_name: department.name,
        password,
      });
      loginDepartment(response.data);
    } catch (error) {
      const blockedDetail = getPaymentBlockedDetail(error);
      if (blockedDetail) {
        showPaymentAccessBlock(blockedDetail);
        return;
      }
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if ([401, 403, 429].includes(status)) {
        throw new Error(typeof detail === 'string' ? detail : 'Ungültiges Passwort');
      }
      throw new Error('Anmeldung derzeit nicht möglich. Bitte erneut versuchen.');
    }
  };

  const handleSetupComplete = () => {
    // After setup is complete, reload the page to load new config.js with title/logo
    // This ensures the frontend picks up the newly generated config.js
    window.location.reload();
  };

  // Shared sleeping domains have no backend, setup or login surface.
  if (forcedPrelaunchSettings) {
    return <PrelaunchPage settings={forcedPrelaunchSettings} />;
  }
  
  // Show loading while checking setup status
  if (checkingSetup || isCheckingMaintenance) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Anwendung...</p>
        </div>
      </div>
    );
  }

  // Prepared but not yet booked app instances retain developer access.
  if (prelaunchMode.is_active) {
    return (
      <>
        <PrelaunchPage
          settings={prelaunchMode}
          onDeveloperAccess={() => window.dispatchEvent(new Event('openOperatorLogin'))}
        />
      </>
    );
  }
  
  // Show Setup Wizard if setup is needed
  if (needsSetup) {
    return <SetupWizard onSetupComplete={handleSetupComplete} />;
  }

  return (
    <>
      <LoginLanding
        departments={departments}
        onLogin={handleDepartmentLogin}
        onBlocked={setBlockedDepartment}
        disabled={maintenanceMode.is_active}
        loading={loadingDepartments}
        loadError={departmentLoadError}
        onRetry={fetchDepartments}
      />
        {/* Show Maintenance Mode Overlay if active - with darker background */}
        {maintenanceMode.is_active && (
          <div className="fixed inset-0 bg-black bg-opacity-85 flex items-center justify-center z-40 p-4">
            <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full text-center">
              <div className="mb-6">
                <svg className="mx-auto h-20 w-20 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Wartungsmodus</h2>
              <p className="text-gray-600 mb-6 whitespace-pre-line">
                {maintenanceMode.message}
              </p>
              <div className="text-sm text-gray-500">
                <p>Die Anwendung ist vorübergehend nicht verfügbar.</p>
                <p className="mt-2">Bitte versuchen Sie es später erneut.</p>
              </div>
              
              {/* Developer Login Access - CRITICAL for maintenance mode access */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => window.dispatchEvent(new Event('openOperatorLogin'))}
                  className="text-xs text-gray-400 hover:text-blue-600 transition-colors duration-200 underline"
                >
                  🔧 Developer Bereich
                </button>
              </div>
            </div>
          </div>
        )}
        
        {blockedDepartment && !maintenanceMode.is_active && (
          <BlockedDepartmentAccessFlow
            department={blockedDepartment}
            verifyAdmin={(department, password) => axios.post(
              `${API}/login/department-admin`,
              {
                department_name: department.name,
                admin_password: password,
              },
            )}
            onClose={() => setBlockedDepartment(null)}
            onAdminLoginSuccess={departmentData => {
              setBlockedDepartment(null);
              loginDepartmentAdmin(departmentData);
            }}
          />
        )}
        
    </>
  );
};

// Login Modal Component
export const LoginModal = ({ title, onLogin, onClose }) => {
  const [password, setPassword] = useState('');
  const [visualViewportStyle, setVisualViewportStyle] = useState(undefined);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;

    const updateVisualViewport = () => {
      const isAtDefaultScale =
        viewport.scale === undefined || Math.abs(viewport.scale - 1) < 0.01;
      const viewportIsShifted =
        isAtDefaultScale && (
          Math.abs(viewport.height - window.innerHeight) > 1 ||
          Math.abs(viewport.width - window.innerWidth) > 1 ||
          Math.abs(viewport.offsetTop) > 1 ||
          Math.abs(viewport.offsetLeft) > 1
        );

      setVisualViewportStyle(viewportIsShifted ? {
        top: `${viewport.offsetTop}px`,
        left: `${viewport.offsetLeft}px`,
        width: `${viewport.width}px`,
        height: `${viewport.height}px`,
        right: 'auto',
        bottom: 'auto',
      } : undefined);
    };

    updateVisualViewport();
    viewport.addEventListener('resize', updateVisualViewport);
    viewport.addEventListener('scroll', updateVisualViewport);

    return () => {
      viewport.removeEventListener('resize', updateVisualViewport);
      viewport.removeEventListener('scroll', updateVisualViewport);
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(password);
    setPassword('');
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 py-8 overflow-y-auto"
      style={visualViewportStyle}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg p-8 max-w-md w-full mx-4 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-2xl font-bold mb-6 text-center">{title}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Passwort
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              required
              autoFocus
            />
          </div>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Anmelden
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Department Dashboard with Admin Login Inside
const DepartmentDashboard = () => {
  const { isSingleDepartment, loading: stationModeLoading } = useStationMode();
  const showOtherDepartments = !isSingleDepartment && !stationModeLoading;
  const [employees, setEmployees] = useState([]);
  const [temporaryEmployees, setTemporaryEmployees] = useState([]); // ERWEITERT für temporäre Mitarbeiter  
  const [eightHourEmployees, setEightHourEmployees] = useState([]); // NEU: 8H-Dienst Mitarbeiter
  const [showNewEmployee, setShowNewEmployee] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeMenuKey, setEmployeeMenuKey] = useState(0); // Key to force EmployeeMenu remount
  const [showEmployeeProfile, setShowEmployeeProfile] = useState(false);
  const [selectedEmployeeForProfile, setSelectedEmployeeForProfile] = useState(null);
  const [showBreakfastSummary, setShowBreakfastSummary] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [showShoppingList, setShowShoppingList] = useState(false);
  const [showRecipes, setShowRecipes] = useState(false);
  const [organizationFeatures, setOrganizationFeatures] = useState(
    HIDDEN_ORGANIZATION_FEATURES
  );
  const [feedbackVersion, setFeedbackVersion] = useState(1);
  const [feedbackPopupShown, setFeedbackPopupShown] = useState(false);
  const [sponsorStatus, setSponsorStatus] = useState({
    breakfast_sponsored: null,
    lunch_sponsored: null
  });
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem('fw_kantine_active_tab_v2') || 'regular'; } catch (e) { return 'regular'; }
  });
  const [nameSearch, setNameSearch] = useState('');
  const {
    currentDepartment,
    logout,
    loginDepartmentAdmin,
    showPaymentAccessBlock,
  } = React.useContext(AuthContext);

  useEffect(() => {
    try { localStorage.setItem('fw_kantine_active_tab_v2', activeTab); } catch (e) {}
  }, [activeTab]);

  useEffect(() => {
    if (!currentDepartment?.department_id) return;
    axios.get(`${API}/departments/${currentDepartment.department_id}/categories`)
      .then(res => setCategories(res.data || []))
      .catch(() => setCategories([]));
  }, [currentDepartment?.department_id]);

  useEffect(() => {
    const departmentId = currentDepartment?.department_id;
    let cancelled = false;
    setOrganizationFeatures(HIDDEN_ORGANIZATION_FEATURES);

    if (!departmentId) return () => { cancelled = true; };

    axios.get(`${API}/department-config/${departmentId}`)
      .then(response => {
        if (!cancelled) {
          setOrganizationFeatures({
            is_shopping_list_enabled:
              response.data.is_shopping_list_enabled === true,
            is_recipe_library_enabled:
              response.data.is_recipe_library_enabled === true,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setOrganizationFeatures(HIDDEN_ORGANIZATION_FEATURES);
      });

    return () => { cancelled = true; };
  }, [currentDepartment?.department_id]);

  // Feedback system storage keys
  const FEEDBACK_GIVEN_KEY = 'fw_kantine_feedback_given';
  const FEEDBACK_VERSION_KEY = 'fw_kantine_feedback_version';
  const FEEDBACK_DISMISSED_KEY = 'fw_kantine_feedback_dismissed';

  // Load feedback version on mount
  useEffect(() => {
    const loadFeedbackVersion = async () => {
      try {
        const response = await axios.get(`${API}/feedback/version`);
        setFeedbackVersion(response.data.version || 1);
      } catch (error) {
        console.warn('Could not load feedback version:', error);
      }
    };
    loadFeedbackVersion();
  }, []);

  // Check if feedback popup should show (before first order)
  // DEAKTIVIERT: Automatisches Popup ist deaktiviert, nur manuelles Feedback über Button
  const checkFeedbackPopup = useCallback(() => {
    // Immer false zurückgeben - kein automatisches Popup mehr
    return false;
    
    /* ALTE LOGIK (deaktiviert):
    const feedbackGiven = localStorage.getItem(FEEDBACK_GIVEN_KEY);
    const storedVersion = localStorage.getItem(FEEDBACK_VERSION_KEY);
    const dismissed = localStorage.getItem(FEEDBACK_DISMISSED_KEY);

    // Don't show if already dismissed this session or popup already shown
    if (dismissed || feedbackPopupShown) return false;

    // Show if version changed (developer reset)
    if (feedbackGiven && storedVersion !== String(feedbackVersion)) {
      return true;
    }

    // First time user
    if (!feedbackGiven) {
      return true;
    }

    return false;
    */
  }, [feedbackVersion, feedbackPopupShown]);

  // Trigger feedback popup before order
  const triggerFeedbackPopupBeforeOrder = useCallback(() => {
    if (checkFeedbackPopup()) {
      setShowFeedbackPopup(true);
      setFeedbackPopupShown(true);
      return true; // Popup shown, should wait
    }
    return false; // No popup, proceed with order
  }, [checkFeedbackPopup]);

  // Submit feedback
  const submitFeedback = useCallback(async ({ rating, comment }) => {
    try {
      await axios.post(`${API}/feedback/submit`, {
        rating,
        comment: comment || '',
        subdomain: window.location.hostname.split('.')[0]
      });
      
      // Mark as given
      localStorage.setItem(FEEDBACK_GIVEN_KEY, 'true');
      localStorage.setItem(FEEDBACK_VERSION_KEY, String(feedbackVersion));
      
      return true;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  }, [feedbackVersion]);

  // Dismiss feedback popup
  const dismissFeedbackPopup = useCallback(() => {
    localStorage.setItem(FEEDBACK_DISMISSED_KEY, 'true');
    setShowFeedbackPopup(false);
  }, []);

  useEffect(() => {
    if (currentDepartment) {
      fetchEmployees();
      fetchTemporaryEmployees();
      fetch8HourEmployees(); // NEU: Lade 8H-Dienst Mitarbeiter
    }
  }, [currentDepartment, showOtherDepartments]);

  const fetch8HourEmployees = async () => {
    try {
      const response = await axios.get(`${API}/departments/${currentDepartment.department_id}/8h-employees`);
      setEightHourEmployees(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der 8H-Mitarbeiter:', error);
    }
  };

  const fetchTemporaryEmployees = async () => {
    try {
      const response = await axios.get(`${API}/departments/${currentDepartment.department_id}/temporary-employees`);
      setTemporaryEmployees(response.data);
    } catch (error) {
      console.error('Fehler beim Laden temporärer Mitarbeiter:', error);
    }
  };

  const removeTemporaryEmployee = async (employeeId) => {
    try {
      // Finde die Assignment-ID des temporären Mitarbeiters
      const tempEmployee = temporaryEmployees.find(emp => emp.id === employeeId);
      if (!tempEmployee?.assignment_id) {
        alert('Fehler: Assignment-ID nicht gefunden');
        return;
      }
      
      await axios.delete(`${API}/departments/${currentDepartment.department_id}/temporary-employees/${tempEmployee.assignment_id}`);
      
      // Lade temporäre Mitarbeiter neu
      await fetchTemporaryEmployees();
      
    } catch (error) {
      console.error('Fehler beim Entfernen:', error);
      alert('Fehler beim Entfernen des Gastmitarbeiters');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(
        `${API}/departments/${currentDepartment.department_id}/employees`
      );
      setEmployees(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
    }
  };

  useEffect(() => {
    const refresh = () => fetchEmployees();
    window.addEventListener('profile-protection-changed', refresh);
    return () => window.removeEventListener('profile-protection-changed', refresh);
  }, [currentDepartment.department_id]);

  const fetchSponsorStatus = async (date) => {
    try {
      const response = await axios.get(`${API}/department-admin/sponsor-status/${currentDepartment.department_id}/${date}`);
      setSponsorStatus(response.data);
    } catch (error) {
      console.error('Fehler beim Laden des Sponsor-Status:', error);
      setSponsorStatus({
        breakfast_sponsored: null,
        lunch_sponsored: null
      });
    }
  };

  const handleEmployeeClick = (employee, event) => {
    // This function opens employee profile/history
    event?.stopPropagation();
    setSelectedEmployeeForProfile(employee);
    setShowEmployeeProfile(true);
  };

  const handleEmployeeProfileClick = async (employee, event) => {
    // This function handles the "Bestellen" button click - should open order menu
    try {
      event.stopPropagation(); // Prevent the employee profile from opening
      
      // Check if feedback popup should be shown (before first order)
      if (triggerFeedbackPopupBeforeOrder()) {
        // Store the employee for later, popup will close and user can click again
        return;
      }
      
      setSelectedEmployee(employee); // Open order menu (correct!)
      setEmployeeMenuKey(prev => prev + 1); // Force remount with new key
    } catch (error) {
      console.error('Error opening employee order menu:', error);
      alert('Fehler beim Öffnen des Bestellmenüs');
    }
  };

  const handleAdminLogin = async (password) => {
    try {
      const response = await axios.post(`${API}/login/department-admin`, {
        department_name: currentDepartment.department_name,
        admin_password: password
      });
      loginDepartmentAdmin(response.data);
      setShowAdminLogin(false);
    } catch (error) {
      const blockedDetail = getPaymentBlockedDetail(error);
      if (blockedDetail) {
        setShowAdminLogin(false);
        showPaymentAccessBlock(blockedDetail);
        return;
      }
      alert('Ungültiges Admin-Passwort');
    }
  };

  const handleCreateEmployee = async (name, isGuest = false, is8HService = false) => {
    try {
      await axios.post(`${API}/employees`, {
        name,
        department_id: currentDepartment.department_id,
        is_guest: isGuest,
        is_8h_service: is8HService
      });
      fetchEmployees();
      fetch8HourEmployees(); // Reload 8H employees if created
      setShowNewEmployee(false);
    } catch (error) {
      console.error('Fehler beim Erstellen des Mitarbeiters:', error);
    }
  };

  if (currentDepartment?.onboarding_required && employees.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100">
        <AnnouncementBanner />
        <div className="flex min-h-[calc(100vh-3rem)] items-center justify-center px-4 py-10">
          <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-sm sm:p-9">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900 text-xl text-white">✓</div>
            <h1 className="mt-5 text-2xl font-semibold text-gray-950">Wachabteilung zuerst einrichten</h1>
            <p className="mt-3 text-sm leading-6 text-gray-600">
              Für {currentDepartment.department_name} sind noch keine Mitarbeiter und Produkte eingerichtet. Ein Administrator kann jetzt die Grundeinstellungen vornehmen und die wichtigsten Funktionen kennenlernen.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button onClick={() => setShowAdminLogin(true)} className="rounded-lg bg-slate-800 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-700">
                Als Admin einrichten
              </button>
              <button onClick={logout} className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Abmelden
              </button>
            </div>
          </div>
        </div>
        {showAdminLogin && (
          <LoginModal
            title={`Admin Login für ${currentDepartment.department_name}`}
            onLogin={handleAdminLogin}
            onClose={() => setShowAdminLogin(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Announcement Banner - always on top */}
      <AnnouncementBanner />
      
      <div className="p-4 sm:p-6 md:p-8 lg:p-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col xl:flex-row xl:justify-between items-center mb-6 sm:mb-8 lg:mb-10 gap-4">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 text-center xl:text-left whitespace-nowrap">
            {currentDepartment.department_name}
          </h1>
          <div className="flex w-full sm:w-auto flex-wrap sm:flex-nowrap items-center justify-center xl:justify-end gap-1 rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
            <EmployeeOrganizationTools
              settings={organizationFeatures}
              onOpenShoppingList={() => setShowShoppingList(true)}
              onOpenRecipes={() => setShowRecipes(true)}
            />
            {!stationModeLoading && <NetworkGuestPicker
              apiBase={API}
              departmentId={currentDepartment.department_id}
              allowLocal={showOtherDepartments}
              temporaryEmployees={temporaryEmployees}
              onAdded={fetchTemporaryEmployees}
            />}

            <FeedbackButton onClick={() => setShowFeedbackModal(true)} />
            <button
              onClick={() => setShowBreakfastSummary(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors whitespace-nowrap"
            >
              <ClipboardList size={17} />
              <span>Essensübersicht</span>
            </button>
            <button
              onClick={() => setShowAdminLogin(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors whitespace-nowrap"
            >
              <ShieldCheck size={17} />
              <span>Admin</span>
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-red-50 hover:text-red-700 transition-colors whitespace-nowrap"
            >
              <LogOut size={17} />
              <span>Abmelden</span>
            </button>
          </div>
        </div>

        {/* v1.16 Suche + Tab-Bar */}
        {(() => {
          const nameMatch = (emp) => {
            const q = (nameSearch || '').trim().toLowerCase();
            if (!q) return true;
            return (emp?.name || '').toLowerCase().includes(q);
          };
          const searchActive = (nameSearch || '').trim().length > 0;
          const cntAll = employees.length + eightHourEmployees.length + temporaryEmployees.length;
          const cntRegular =
            employees.filter(isRegularDepartmentEmployee).length +
            eightHourEmployees.filter(isRegularDepartmentEmployee).length +
            temporaryEmployees.filter(e => !e.is_external_guest).length;
          const cntExternal = temporaryEmployees.filter(e => e.is_external_guest).length;
          const getContrastTextColor = (hexColor) => {
            const shortOrFullHex = (hexColor || '').replace('#', '');
            const hex = shortOrFullHex.length === 3
              ? shortOrFullHex.split('').map(char => `${char}${char}`).join('')
              : shortOrFullHex;
            if (!/^[0-9a-f]{6}$/i.test(hex)) return '#FFFFFF';

            const channels = [0, 2, 4].map(index => parseInt(hex.slice(index, index + 2), 16) / 255);
            const [red, green, blue] = channels.map(value =>
              value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4)
            );
            const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
            const whiteContrast = 1.05 / (luminance + 0.05);
            const darkContrast = (luminance + 0.05) / 0.05;
            return darkContrast >= whiteContrast ? '#111827' : '#FFFFFF';
          };
          const tabDefs = [
            { key: 'regular', label: 'Regulär', color: null, count: cntRegular },
            { key: 'external', label: 'Andere Wachen', color: null, count: cntExternal },
            ...categories.map(c => ({
              key: `cat-${c.id}`,
              label: c.name,
              color: c.color || '#6B7280',
              count:
                employees.filter(e => e.category_id === c.id).length +
                eightHourEmployees.filter(e => e.category_id === c.id).length,
            })),
            { key: 'all', label: 'Alle', color: null, count: cntAll },
          ].filter(t => t.key === 'all' || t.count > 0);
          return (
            <div className="px-4 mb-4 space-y-3">
              <div className="relative">
                <input
                  type="text"
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.target.value)}
                  placeholder="🔍 Mitarbeiter suchen…"
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                  data-testid="employee-search-input"
                />
                {nameSearch && (
                  <button
                    type="button"
                    onClick={() => setNameSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-xl leading-none"
                    title="Suche zurücksetzen"
                    data-testid="employee-search-clear"
                  >×</button>
                )}
              </div>
              {!searchActive && (
                <div className="flex flex-wrap gap-1.5 sm:gap-2 pb-1" data-testid="employee-category-tabs">
                  {tabDefs.map(t => {
                    const isActive = activeTab === t.key;
                    const style = isActive && t.color ? {
                      backgroundColor: t.color,
                      borderColor: t.color,
                      color: getContrastTextColor(t.color),
                    } : {};
                    return (
                      <button
                        key={t.key}
                        onClick={() => setActiveTab(t.key)}
                        style={style}
                        className={`whitespace-nowrap px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm border transition-all ${
                          isActive ? (t.color ? '' : 'bg-blue-600 border-blue-600 text-white') : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                        data-testid={`category-tab-${t.key}`}
                      >
                        {t.label} <span className="opacity-70">({t.count})</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {searchActive && <p className="text-xs text-gray-500">Suche aktiv — Alle Mitarbeiter werden durchsucht.</p>}
            </div>
          );
        })()}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-5 px-4">
          {(() => {
            const nameMatch = (emp) => {
              const q = (nameSearch || '').trim().toLowerCase();
              if (!q) return true;
              return (emp?.name || '').toLowerCase().includes(q);
            };
            const searchActive = (nameSearch || '').trim().length > 0;
            const isCatTab = activeTab.startsWith('cat-');
            const catId = isCatTab ? activeTab.slice(4) : null;
            const allPeople = [
              ...employees.map(emp => ({ ...emp, _displayType: emp.is_guest ? 'guest' : 'regular' })),
              ...eightHourEmployees.map(emp => ({ ...emp, _displayType: '8h' })),
              ...temporaryEmployees.map(emp => ({ ...emp, _displayType: 'temporary' })),
            ];
            const regularPeople = [
              ...allPeople.filter(emp => (
                (emp._displayType === 'regular' || emp._displayType === 'guest' || emp._displayType === '8h')
                && isRegularDepartmentEmployee(emp)
              )),
              ...allPeople.filter(emp => emp._displayType === 'temporary' && !emp.is_external_guest),
            ];

            const renderSectionHeader = (label, count, color = '#6B7280') => (
              <div className="col-span-full flex items-center gap-3 mt-3 mb-1" key={`section-${label}`}>
                <div className="h-px flex-1 bg-gray-200"></div>
                <div className="flex items-center gap-2 px-2 text-sm font-semibold text-gray-700">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }}></span>
                  <span>{label}</span>
                  <span className="text-xs font-normal text-gray-400">({count})</span>
                </div>
                <div className="h-px flex-1 bg-gray-200"></div>
              </div>
            );

            const renderPersonCard = (employee) => {
              const displayType = employee._displayType;
              const isTemporary = displayType === 'temporary';
              const assignedCategory = categories.find(category => category.id === employee.category_id);
              const categoryColor = assignedCategory?.color || null;
              const fallbackAccentColor = isTemporary ? '#C084FC' : null;
              const accentColor = categoryColor || fallbackAccentColor;
              const getContrastColor = (color) => {
                const hex = color?.replace('#', '');
                if (!hex || !/^[0-9a-fA-F]{6}$/.test(hex)) return '#FFFFFF';
                const red = parseInt(hex.slice(0, 2), 16);
                const green = parseInt(hex.slice(2, 4), 16);
                const blue = parseInt(hex.slice(4, 6), 16);
                return ((red * 299 + green * 587 + blue * 114) / 1000) > 160
                  ? '#111827'
                  : '#FFFFFF';
              };
              const externalDisplay = externalGuestDisplay(employee);
              const technicalLabel = externalDisplay?.label || (isTemporary ? 'Andere WA' : null);

              return (
                <div
                  key={`${displayType}-${employee.id}`}
                  onClick={(event) => handleEmployeeClick(employee, event)}
                  className={`bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 cursor-pointer transition-shadow hover:shadow-md ${accentColor ? 'border-l-4' : ''}`}
                  style={accentColor ? { borderLeftColor: accentColor } : undefined}
                >
                  <div className="min-w-0 mb-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="min-w-0 flex-1 text-base lg:text-lg leading-tight font-semibold text-gray-800 truncate" title={employee.name}>
                        {employee.name}
                        {employee.personal_password_enabled && (employee.is_8h_service || employee.department_id===currentDepartment.department_id) && !employee.is_external_guest && !employee.is_guest && <LockKeyhole className="ml-2 inline h-3.5 w-3.5 text-emerald-700" aria-label="Bestellschutz aktiv"/>}
                      </h3>
                      {technicalLabel && (
                        <span className="shrink-0 text-xs font-medium text-gray-500">
                          {technicalLabel}
                        </span>
                      )}
                    </div>
                    {isTemporary && (
                      <p className="mt-1 text-xs text-gray-500 truncate">
                        {externalDisplay?.department || employee.department_name}
                      </p>
                    )}
                  </div>
                  <div className={`grid gap-2 border-t border-gray-100 pt-2 ${isTemporary ? 'grid-cols-[1fr_1fr_auto]' : 'grid-cols-2'}`}>
                    <button
                      type="button"
                      className="min-h-9 text-center text-xs sm:text-sm font-medium text-gray-600 px-3 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                      onClick={(event) => handleEmployeeClick(employee, event)}
                    >
                      Verlauf
                    </button>
                    <button
                      type="button"
                      onClick={(event) => handleEmployeeProfileClick(employee, event)}
                      className={`min-h-9 text-xs sm:text-sm py-1.5 px-3 rounded-lg transition-all font-medium ${categoryColor ? 'hover:brightness-90' : 'bg-gray-800 hover:bg-gray-900 text-white'}`}
                      style={categoryColor ? {
                        backgroundColor: categoryColor,
                        color: getContrastColor(categoryColor),
                      } : undefined}
                    >
                      Bestellen
                    </button>
                    {isTemporary && (
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          removeTemporaryEmployee(employee.id);
                        }}
                        className="min-h-9 text-gray-400 hover:text-red-700 hover:bg-red-50 rounded-lg px-2 py-1.5 text-xs transition-colors"
                        title="Gastmitarbeiter entfernen"
                        aria-label={`${employee.name} aus der Tagesansicht entfernen`}
                      >
                        ✗
                      </button>
                    )}
                  </div>
                </div>
              );
            };

            let sections = [];
            if (searchActive) {
              sections = [{ key: 'search', people: allPeople.filter(nameMatch) }];
            } else if (activeTab === 'all') {
              if (regularPeople.length > 0) {
                sections.push({ key: 'regular', label: 'Regulär', color: '#6B7280', people: regularPeople });
              }
              categories.forEach(category => {
                const people = allPeople.filter(emp => emp.category_id === category.id);
                if (people.length > 0) {
                  sections.push({
                    key: `category-${category.id}`,
                    label: category.name,
                    color: category.color || '#6B7280',
                    people,
                  });
                }
              });
              const externalPeople = allPeople.filter(emp => emp.is_external_guest);
              if (externalPeople.length) sections.push({key:'external', label:'Andere Wachen', color:'#64748B', people:externalPeople});
            } else if (activeTab === 'external') {
              sections = [{key:'external', people:allPeople.filter(emp => emp.is_external_guest)}];
            } else if (activeTab === 'regular') {
              sections = [{ key: 'regular', people: regularPeople }];
            } else if (isCatTab) {
              sections = [{
                key: `category-${catId}`,
                people: allPeople.filter(emp => emp.category_id === catId),
              }];
            }

            return (
              <>
                {sections.map(section => (
                  <React.Fragment key={section.key}>
                    {activeTab === 'all' && section.label && renderSectionHeader(section.label, section.people.length, section.color)}
                    {section.people.map(renderPersonCard)}
                  </React.Fragment>
                ))}
              </>
            );
          })()}
        </div>

        {/* Breakfast Summary Modal */}
        {showShoppingList && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4 sm:p-8" onClick={() => setShowShoppingList(false)}>
            <div className="mx-auto max-w-4xl" onClick={(event) => event.stopPropagation()}>
              <ShoppingList departmentId={currentDepartment.department_id} onClose={() => setShowShoppingList(false)} />
            </div>
          </div>
        )}

        {showRecipes && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4 sm:p-8" onClick={() => setShowRecipes(false)}>
            <div className="mx-auto max-w-4xl" onClick={(event) => event.stopPropagation()}>
              <RecipeLibrary departmentId={currentDepartment.department_id} onClose={() => setShowRecipes(false)} />
            </div>
          </div>
        )}

        {showBreakfastSummary && (
          <BreakfastSummaryTable
            departmentId={currentDepartment.department_id}
            onClose={() => setShowBreakfastSummary(false)}
          />
        )}

        {/* Employee Profile Modal for Individual Employee */}
        {showEmployeeProfile && selectedEmployeeForProfile && (
          <IndividualEmployeeProfile
            employee={selectedEmployeeForProfile}
            onClose={() => {
              setShowEmployeeProfile(false);
              setSelectedEmployeeForProfile(null);
            }}
          />
        )}

        {/* Employee Menu Modal */}
        {selectedEmployee && (
          <EmployeeMenu
            key={employeeMenuKey}
            employee={selectedEmployee}
            onClose={() => setSelectedEmployee(null)}
            onOrderComplete={() => {
              fetchEmployees();
              setSelectedEmployee(null);
            }}
            fetchEmployees={fetchEmployees}
          />
        )}

        {/* Admin Login Modal */}
        {showAdminLogin && (
          <LoginModal
            title={`Admin Login für ${currentDepartment.department_name}`}
            onLogin={handleAdminLogin}
            onClose={() => setShowAdminLogin(false)}
          />
        )}

        {/* New Employee Modal */}
        {showNewEmployee && (
          <NewEmployeeModal
            onCreate={handleCreateEmployee}
            onClose={() => setShowNewEmployee(false)}
          />
        )}

        {/* Feedback Modal (manual button click) */}
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={submitFeedback}
          isPopup={false}
        />

        {/* Feedback Popup (automatic, before first order) */}
        <FeedbackModal
          isOpen={showFeedbackPopup}
          onClose={dismissFeedbackPopup}
          onSubmit={async (data) => {
            await submitFeedback(data);
            setShowFeedbackPopup(false);
          }}
          isPopup={true}
        />
      </div>
      </div>
      
      {/* Scroll to Top Button */}
      <ScrollToTop />
    </div>
  );
};

// Employee Menu Component

// Import extracted components
import EmployeeMenu from './components/Employee/EmployeeMenu';
import DepartmentAdminDashboard from './components/Admin/DepartmentAdminDashboard';
import { BreakfastSummaryTable } from './components/Admin/AdminComponents';
import DeveloperDashboard from './components/Developer/DeveloperDashboard';
import PrelaunchPage from './components/Common/PrelaunchPage';

function App() {
  const {
    currentDepartment,
    isDepartmentAdmin,
    isInitializing,
    maintenanceMode,
    prelaunchMode,
    logout,
    operatorSession,
    activateOperatorSession,
    openOperatorDeveloper,
    openOperatorDepartment
  } = React.useContext(AuthContext);
  const [showOperatorLogin, setShowOperatorLogin] = useState(false);

  const handleOperatorApproved = useCallback((session) => {
    activateOperatorSession(session);
    setShowOperatorLogin(false);
  }, [activateOperatorSession]);

  // Listen for Developer Login event from footer (global)
  useEffect(() => {
    const handleOpenDeveloperLogin = () => {
      if (operatorSession) {
        openOperatorDeveloper();
      } else {
        setShowOperatorLogin(true);
      }
    };
    
    window.addEventListener('openDeveloperLogin', handleOpenDeveloperLogin);
    window.addEventListener('openOperatorLogin', handleOpenDeveloperLogin);
    
    return () => {
      window.removeEventListener('openDeveloperLogin', handleOpenDeveloperLogin);
      window.removeEventListener('openOperatorLogin', handleOpenDeveloperLogin);
    };
  }, [operatorSession, openOperatorDeveloper]);

  // Update footer version from central version.js
  useEffect(() => {
    const versionElement = document.getElementById('app-version');
    if (versionElement) {
      versionElement.textContent = 'v' + APP_VERSION;
    }
  }, []);

  // Handle page refresh to maintain current user context and scroll to top
  useEffect(() => {
    // Always scroll to top on page load/refresh
    window.scrollTo(0, 0);

    const handlePageRefresh = () => {
      // The localStorage persistence will automatically restore the user to their last department/admin state
      // No additional action needed - just let the AuthProvider handle initialization
    };

    // Optional: Add refresh detection for future enhancements
    const detectRefresh = () => {
      if (performance.navigation.type === performance.navigation.TYPE_RELOAD) {
        handlePageRefresh();
      }
    };

    detectRefresh();
  }, []);

  useEffect(() => installHomeNavigation(
    document,
    () => navigateToActiveDashboard(window.location),
  ), []);

  // Auto-logout normal users when this instance is not in normal operation.
  useEffect(() => {
    const accessIsRestricted = maintenanceMode.is_active || prelaunchMode.is_active;
    if (accessIsRestricted && currentDepartment && !operatorSession && currentDepartment.role !== 'developer') {
      logout();
      alert(
        maintenanceMode.is_active
          ? '⚠️ Wartungsmodus aktiviert – Sie wurden automatisch abgemeldet.'
          : 'Diese Wache befindet sich aktuell im Interessentenmodus.'
      );
    }
  }, [
    maintenanceMode.is_active,
    prelaunchMode.is_active,
    currentDepartment,
    operatorSession,
    logout
  ]);
  
  // Title is now static in index.html - no dynamic loading needed
  // If you need to change the title, edit /app/frontend/public/index.html manually


  // Show loading screen ONLY while initializing from localStorage
  // Don't wait for maintenance check - it can happen in background
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Anwendung...</p>
        </div>
      </div>
    );
  }

  const operatorControls = (
    <>
      {operatorSession && (
        <OperatorSessionBar
          session={operatorSession}
          onDeveloper={openOperatorDeveloper}
          onDepartment={openOperatorDepartment}
          onLogout={logout}
        />
      )}
      {showOperatorLogin && (
        <OperatorLoginDialog
          onApproved={handleOperatorApproved}
          onClose={() => setShowOperatorLogin(false)}
        />
      )}
    </>
  );

  if (
    currentDepartment?.role === 'developer' ||
    (currentDepartment?.role === 'operator' && currentDepartment?.operator_view === 'developer')
  ) {
    return (
      <>
        <DeveloperDashboard />
        {operatorControls}
      </>
    );
  }

  if (isDepartmentAdmin) {
    return (
      <>
        <DepartmentAdminDashboard />
        {operatorControls}
      </>
    );
  }

  if (currentDepartment) {
    return (
      <>
        <DepartmentDashboard />
        {operatorControls}
      </>
    );
  }

  return (
    <>
      <Homepage />
      {operatorControls}
    </>
  );
}


// App with Context Provider
export default function AppWithProvider() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}
